import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { rotateDeltaToLocal } from '@/components/app/editor/tools/shared/helpers/rotate-delta-to-local'
import type { CropFrameGeom } from '@/lib/photos/crop-frame-resize'
import { computeCropPhotoPan } from '@/lib/photos/crop-photo-pan'
import { visualRectLocalOfShape } from '@/lib/shapes/shape-coordinates'
import type { PanDrag } from '../types/crop-drag'

/** The photo fill after panning by canvas-space delta (`dx`, `dy`) since the pan drag started. */
export function computePannedPhotoFill(
  shape: ShapeData,
  geom: CropFrameGeom,
  drag: PanDrag,
  initialPhotoFill: ShapePhotoFill,
  dx: number,
  dy: number
): ShapePhotoFill {
  // Pan: convert the canvas-space delta into the photo's own pre-rotation local space (same
  // technique NodeEditOverlay uses for anchor drags) so panning "down" always moves the
  // photo down on screen — regardless of the SHAPE's own rotation, AND regardless of the
  // photo CONTENT's own rotation (`photoFill.rotation`, set by this overlay's separate
  // photo-rotate dial). Both are undone together: for a pure delta/direction vector (as
  // opposed to a point), two rotations compose by simple angle addition regardless of their
  // respective pivots, since there's no translation component to a direction. Omitting
  // `photoFill.rotation` here left the photo's own rotation completely uncompensated, so
  // panning felt inverted (up moved it down) once that rotation passed roughly 90°+ (WMPCM
  // "move tool acts inverted after rotating the image inside the shape").
  const rot = (shape.rotation ?? 0) + (initialPhotoFill.rotation ?? 0)
  const { dx: localDx, dy: localDy } = rotateDeltaToLocal(dx, dy, rot)

  // Compute from the fixed drag-start offset + total delta-since-start (not the previous
  // frame's already-clamped offset) so repeated clamping across frames can't ratchet the
  // pan short of the real slack boundary.
  //
  // Clamped against the shape's tight VISUAL box, not its full geometric frame — a
  // non-rectangular shape (triangle, star, custom path) only needs the photo to cover what's
  // actually visible once clipped; requiring it to also cover the (invisible, clipped-away)
  // corners of the bounding box wastes real pan range, and — since the visual box can sit
  // off-center within that bounding box — can block one direction well before the shape's
  // own edge while leaving slack unused on the other.
  const { offsetX, offsetY } = computeCropPhotoPan({
    frameWidth: geom.width,
    frameHeight: geom.height,
    visualRect: visualRectLocalOfShape({
      ...shape,
      width: geom.width,
      height: geom.height,
    }),
    photoFill: {
      ...initialPhotoFill,
      offsetX: drag.initialOffsetX,
      offsetY: drag.initialOffsetY,
    },
    localDx,
    localDy,
  })

  return { ...initialPhotoFill, offsetX, offsetY }
}
